import { FormField } from '@/global/components/Form'
import ControlledTextField from '@/global/components/Form/Base/ControlledTextField'
import { periodicidade } from '@/locales/plan'
import { checkoutSchemas } from '@/templates/Checkout/constants/checkoutSchemas'
import { CheckoutFormData } from '@/templates/Checkout/constants/types'
import AbasFormaPagamento from '@/templates/Checkout/forms/Pagamento/FormaPagamentoAbas'
import useCheckoutForm from '@/templates/Checkout/hooks/useCheckoutForm'
import { omit } from '@acme/common'
import { invariant } from '@acme/core'
import { Grid } from '@mui/material'
import Dialog from 'global/guides/Dialog'
import { usePlanosContext } from 'pages/Planos'
import { useMemo, useState } from 'react'
import { assinaturas } from 'service/api'
import { toast } from 'sonner'
import { z } from 'zod'

const cardSchema = z
    .object({
        mainPaymentMethodId: z.string(),
        Card: z
            .union([
                checkoutSchemas.pagamento._def.schema.shape.Card,
                z.object({
                    myId: z.string(),
                }),
            ])
            .optional(),
    })
    .superRefine((data, ctx) => {
        // Só valida o cartão se o método de pagamento for cartão de crédito
        if (data.mainPaymentMethodId === 'creditcard') {
            if (!data.Card) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message:
                        'Cartão é obrigatório para pagamento com cartão de crédito',
                    path: ['Card'],
                })
                return
            }

            // Valida se é cartão novo ou existente
            if ('myId' in data.Card) {
                if (!data.Card.myId || data.Card.myId.trim() === '') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Selecione um cartão',
                        path: ['Card', 'myId'],
                    })
                }
            }
        }
    })

export default function AlterarMetodoPagamento() {
    const [isLoading, setIsLoading] = useState(false)
    const {
        planos = [],
        assinatura,
        PlanoDiferenciado,
        cliente,
    } = usePlanosContext()

    const meuPlano = useMemo(() => {
        if (PlanoDiferenciado) {
            return {
                limiteFuncionarios: PlanoDiferenciado.limiteFuncionarios,
                periodicity: PlanoDiferenciado.periodicidade,
                price: PlanoDiferenciado.valorCentavos,
            }
        }
        return planos.find(
            it =>
                it.limiteFuncionarios ===
                    Number(assinatura?.additionalInfo?.replace(/\D/g, '')) &&
                it.periodicity === assinatura?.periodicity,
        )
    }, [planos, assinatura])

    const descricao = useMemo(() => {
        const nenhum = !assinatura || !meuPlano
        if (nenhum) return 'Detalhes do plano e assinatura não encontrados'
        else if (!assinatura) return 'Detalhes da assinatura não encontrados'
        else return 'Detalhes do plano não encontrados'
    }, [assinatura, meuPlano])

    if (!assinatura || !meuPlano) {
        return (
            <Dialog.Root height="auto">
                <Dialog.Title>{descricao}</Dialog.Title>
                <Dialog.Actions>
                    <Dialog.Actions.SecondaryButton>
                        Voltar
                    </Dialog.Actions.SecondaryButton>
                </Dialog.Actions>
            </Dialog.Root>
        )
    }

    async function handleSubmit(data: CheckoutFormData) {
        invariant(!!assinatura?.myId, 'Assinatura não encontrada')
        setIsLoading(true)
        return assinaturas
            .patch(assinatura.myId, {
                mainPaymentMethodId: data.mainPaymentMethodId,
                ...(data.mainPaymentMethodId === 'creditcard' && {
                    PaymentMethodCreditCard: {
                        Card: data.Card.myId
                            ? { myId: data.Card.myId }
                            : omit(data.Card, 'myId'),
                    },
                }),
            })
            .then(async () => {
                toast.success('Método de pagamento alterado com sucesso!')

                setTimeout(() => {
                    setIsLoading(false)
                    window.location.reload()
                }, 2000)
            })
            .catch(error => {
                toast.error(
                    'Erro ao alterar método de pagamento: ' + error.message,
                )
                setIsLoading(false)
            })
    }

    return (
        <Dialog.Root
            loading={isLoading}
            defaultValues={{
                Plan: meuPlano,
                Customer: { myId: cliente?.myId },
                mainPaymentMethodId: assinatura.mainPaymentMethodId,
            }}
            onSubmit={handleSubmit}
            schema={cardSchema}
        >
            <Dialog.Title>Alterar método de pagamento</Dialog.Title>
            <Dialog.Content dividers>
                <DetalhesPlano />
                <ControlledTextField
                    name="_formaAtual"
                    label="Forma de pagamento atual"
                    value={
                        assinatura.mainPaymentMethodId === 'creditcard'
                            ? 'Cartão de crédito'
                            : 'Boleto'
                    }
                    disabled
                    variant="filled"
                />
                <AbasFormaPagamento
                    disabled={
                        assinatura.mainPaymentMethodId === 'boleto'
                            ? 'boleto'
                            : undefined
                    }
                />
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton />
            </Dialog.Actions>
        </Dialog.Root>
    )
}

function DetalhesPlano() {
    const { watch } = useCheckoutForm()
    const plano = watch('Plan')

    if (!plano) return null

    return (
        <Grid container spacing={2} columns={{ sm: 12, xs: 8 }}>
            <Grid item xs={8}>
                <ControlledTextField
                    variant="filled"
                    label="Plano atual"
                    value={`até ${plano.limiteFuncionarios} funcionários (${
                        periodicidade[plano.periodicity]
                    })`}
                    disabled
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <FormField
                    name={`Plan.price`}
                    label="Preço"
                    disabled
                    currency="BRL"
                />
            </Grid>
        </Grid>
    )
}
